import * as React from 'react';
import { X, AlertCircle } from 'lucide-react';
import type { UnderLease } from '../../types/underlease';
import { useClickOutside } from '../../hooks/useClickOutside';
import { api } from '../../services/api';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import type { LotLeaseSummary } from '../../services/supabase/lot-lease-summary';
import type { Leaseholder } from '../../types/leaseholder';

// Simplified Sublease type with just the data we need for the dropdown
interface SimpleSublease {
  lease_id: number;
  lot_id?: string;
  community_name?: string;
  holder_name?: string;
}

interface UnderLeaseFormModalProps {
  lease?: UnderLease; // Optional - if provided, we're editing an existing underlease
  onClose: () => void;
}

export default function UnderLeaseFormModal({ lease, onClose }: UnderLeaseFormModalProps) {
  const modalContentRef = React.useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose);

  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    underlease_lease: lease?.underlease_lease?.toString() || '',
    underlease_holder: lease?.underlease_holder?.toString() || '',
    underlease_start: lease?.underlease_start ? new Date(lease.underlease_start).toISOString().split('T')[0] : '',
    underlease_end: lease?.underlease_end ? new Date(lease.underlease_end).toISOString().split('T')[0] : '',
    underlease_use: lease?.underlease_use || ''
  });
  const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});

  // Load available sublease IDs and leaseholders for dropdowns
  const { data: subleaseIds, isLoading: isLoadingSubleases, error: subleasesError } = useQuery<number[]>({
    queryKey: ['subleaseIds'],
    queryFn: () => api.leases.getSubleaseIds()
  });

  const { data: lotLeaseSummary } = useQuery<LotLeaseSummary[]>({
    queryKey: ['lotLeaseSummary'],
    queryFn: () => api.lotLeaseSummary.getAll()
  });

  const { data: leaseholders, isLoading: isLoadingLeaseholders, error: leaseholdersError } = useQuery<Leaseholder[]>({
    queryKey: ['leaseholders'],
    queryFn: () => api.leaseholders.getAll()
  });

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.underlease_lease) {
      errors.underlease_lease = 'Sub-lease is required';
    }
    
    if (!formData.underlease_holder) {
      errors.underlease_holder = 'Leaseholder is required';
    }
    
    if (!formData.underlease_start) {
      errors.underlease_start = 'Start date is required';
    }
    
    if (!formData.underlease_end) {
      errors.underlease_end = 'End date is required';
    }
    
    if (formData.underlease_start && formData.underlease_end && 
        new Date(formData.underlease_start) > new Date(formData.underlease_end)) {
      errors.underlease_end = 'End date must be after start date';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        underlease_lease: parseInt(formData.underlease_lease),
        underlease_holder: parseInt(formData.underlease_holder)
      };

      if (lease) {
        // Update existing underlease
        await api.underleases.update(
          lease.underlease_lease,
          lease.underlease_holder,
          lease.underlease_start,
          payload
        );
      } else {
        // Create new underlease
        await api.underleases.create(payload);
      }

      // Invalidate and refetch with the correct query key
      await queryClient.invalidateQueries({ queryKey: ['admin', 'underleases'] });
      onClose();
    } catch (err) {
      console.error('Error saving underlease:', err);
      setError(err instanceof Error ? err.message : 'Failed to save under-lease');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when field is changed
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-3xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] overflow-y-auto flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">
            {lease ? 'Edit Under-Lease' : 'Add New Under-Lease'}
          </h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form id="under-lease-form" onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Under-Lease Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Sub-Lease *
                </label>
                <div className="relative mt-1">
                  {isLoadingSubleases ? (
                    <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-500 text-sm">
                      Loading subleases...
                    </div>
                  ) : subleasesError ? (
                    <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      <span>Error loading subleases</span>
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        name="underlease_lease"
                        value={formData.underlease_lease}
                        onChange={handleChange}
                        required
                        className={`block w-full rounded-md ${
                          validationErrors.underlease_lease 
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:border-primary focus:ring-primary'
                        } shadow-sm sm:text-sm appearance-none bg-white cursor-pointer hover:border-primary`}
                        style={{ 
                          backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                          backgroundPosition: "right 0.5rem center",
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "1.5em 1.5em",
                          paddingRight: "2.5rem"
                        }}
                      >
                        <option value="">Select a sub-lease</option>
                        {subleaseIds?.map((leaseId) => (
                          <option key={leaseId} value={leaseId}>
                            {`Sublease ID: ${leaseId}`}
                          </option>
                        ))}
                      </select>
                      
                      {/* Add a visual indicator for the dropdown */}
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      
                      {/* Removed green/red dot indicator */}
                    </div>
                  )}
                </div>
                {validationErrors.underlease_lease && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.underlease_lease}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">Select the sublease for this under-lease</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Leaseholder *
                </label>
                <div className="relative mt-1">
                  {isLoadingLeaseholders ? (
                    <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-500 text-sm">
                      Loading leaseholders...
                    </div>
                  ) : leaseholdersError ? (
                    <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      <span>Error loading leaseholders</span>
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        name="underlease_holder"
                        value={formData.underlease_holder}
                        onChange={handleChange}
                        required
                        className={`block w-full rounded-md ${
                          validationErrors.underlease_holder 
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:border-primary focus:ring-primary'
                        } shadow-sm sm:text-sm appearance-none bg-white cursor-pointer hover:border-primary`}
                        style={{ 
                          backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                          backgroundPosition: "right 0.5rem center",
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "1.5em 1.5em",
                          paddingRight: "2.5rem"
                        }}
                      >
                        <option value="">Select a leaseholder</option>
                        {leaseholders?.map((holder: Leaseholder) => (
                          <option key={holder.holder_id} value={holder.holder_id}>
                            {holder.holder_name}
                          </option>
                        ))}
                      </select>
                      
                      {/* Add a visual indicator for the dropdown */}
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                {validationErrors.underlease_holder && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.underlease_holder}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">Select the leaseholder for this under-lease</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="underlease_start"
                  value={formData.underlease_start}
                  onChange={handleChange}
                  required
                  className={`mt-1 block w-full rounded-md ${
                    validationErrors.underlease_start 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:border-primary focus:ring-primary'
                  } shadow-sm sm:text-sm`}
                />
                {validationErrors.underlease_start && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.underlease_start}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">The date when the under-lease begins</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">
                  End Date *
                </label>
                <input
                  type="date"
                  name="underlease_end"
                  value={formData.underlease_end}
                  onChange={handleChange}
                  required
                  className={`mt-1 block w-full rounded-md ${
                    validationErrors.underlease_end 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:border-primary focus:ring-primary'
                  } shadow-sm sm:text-sm`}
                />
                {validationErrors.underlease_end && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.underlease_end}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">The date when the under-lease expires</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500">
                  Permitted Use *
                </label>
                <input
                  type="text"
                  name="underlease_use"
                  value={formData.underlease_use}
                  onChange={handleChange}
                  required
                  className={`mt-1 block w-full rounded-md ${
                    validationErrors.underlease_use 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:border-primary focus:ring-primary'
                  } shadow-sm sm:text-sm`}
                  placeholder="Enter permitted use"
                />
                {validationErrors.underlease_use && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.underlease_use}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">Specify the allowed use for this under-lease</p>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="under-lease-form"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : lease ? 'Save Changes' : 'Create Under-Lease'}
          </button>
        </div>
      </div>
    </div>
  );
}
